import { useEffect } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useOffice } from 'src/features/plugins/outlook/contexts/OfficeContext';
import LoadingPage from 'src/features/plugins/outlook/components/LoadingPage';

const auth0Domain = import.meta.env.VITE_AUTH0_BASE_URL;

export default function DialogRedirectPage() {
  const searchParams = useSearch({ strict: false }) as Record<string, string | undefined>;
  const { isOfficeReady } = useOffice();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isOfficeReady) return;
    const redirectParam = searchParams['redirect'] ?? null;

    function constructValidatedURL(urlInput: string): URL | null {
      try {
        const url = new URL(urlInput);
        if (url.hostname !== auth0Domain) {
          return null;
        }

        const validatedURLBase = new URL(`https://${auth0Domain}`);

        if (url.pathname === '/authorize' || url.pathname === '/v2/logout') {
          validatedURLBase.pathname = url.pathname;
          validatedURLBase.search = url.search;
          return validatedURLBase;
        } else {
          return null;
        }
      } catch (error) {
        console.error(
          `[DialogRedirectPage] Error constructing or validating URL. Error: ${error}, Input was: ${urlInput}`
        );
        return null;
      }
    }

    if (redirectParam) {
      const validatedURL = constructValidatedURL(redirectParam);
      if (validatedURL) {
        console.log('[DialogRedirectPage] Redirect param detected, navigating to validated URL:', validatedURL.href);
        window.location.href = validatedURL.href;
      } else {
        console.warn('[DialogRedirectPage] Redirect param rejected by validation:', redirectParam);
      }
    } else {
      const message = {
        status: 'success',
        urlWithAuthInfo: window.location.href,
      };

      const sendMessage = () => {
        try {
          if (typeof Office !== 'undefined' && Office.context && Office.context.ui) {
            console.log('[DialogRedirectPage] Sending message to parent window:', message);
            Office.context.ui.messageParent(JSON.stringify(message));
          } else {
            console.warn('[DialogRedirectPage] Office context not available when attempting to message parent.');
          }
        } catch (error) {
          console.error('Error sending message to parent', error);
        }
      };

      sendMessage();
      setTimeout(() => sendMessage(), 500);
      window.addEventListener('beforeunload', sendMessage);
    }
  }, [searchParams, isOfficeReady]);

  return <LoadingPage />;
}
